import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import { get, set } from "@totalsoft/change-tracking-react";
import { QUOTE_WITH_CONTACT_PERSON_QUERY } from "features/quote/queries";
import { useTranslation } from "react-i18next";
import { emptyArray, emptyString } from "constants/defaultValues";
import { useMutationWithErrorHandling } from "hooks/errorHandling";
import { SAVE_PARTNER_CONTACT_PERSON } from "../mutations";
import ActionsDialog from "../../common/components/ActionsDialog";
import { isDirty } from "@totalsoft/change-tracking";
import { Grid, makeStyles, Typography, Link, Button, CircularProgress, CardContent } from "@material-ui/core";
import styles from "../styles";
import { Autocomplete, Card, CustomTextField } from "@totalsoft/rocket-webapp-components-deprecated";
import { getErrors, isValid } from "@totalsoft/pure-validations";
import { onTextBoxChange } from "utils/propertyChangeAdapters";
import { useParams } from "react-router";
import CheckIcon from "@material-ui/icons/Check";
import { partnerTypes } from "constants/partnerTypes";

const useStyles = makeStyles(styles);

function CustomDataContainer(props) {
  const { localStateLens, localStateDirtyInfo, resetLocalStateLens } = props;
  const { validation, validate, disabled } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const { quoteId } = useParams();
  const [partnerContactPersons, setPartnerContactPersons] = useState(emptyArray);

  const contactInfoReadOnly = useMemo(() => {
    return (
      (localStateLens?.contact?.webContactInfoId |> get) > 0 || !(localStateLens?.contact?.webContactInfoId |> get)
    );
  }, [localStateLens?.contact?.webContactInfoId]);

  const [actionsDialogProps, setActionsDialogProps] = useState({
    open: false,
    title: emptyString,
    message: emptyString,
    actions: emptyArray,
  });
  const handleCloseActionsDialog = useCallback(() => setActionsDialogProps((prev) => ({ ...prev, open: false })), []);

  const { data } = useQueryWithErrorHandling(QUOTE_WITH_CONTACT_PERSON_QUERY, {
    variables: { quoteId: parseInt(quoteId) },
    onCompleted: (data) => {
      if (!data) return;
      const contactPersons = data.quote.contactPersons.map((item) => {
        return { ...item, fullName: item.firstName + " " + item.lastName };
      });
      setPartnerContactPersons(contactPersons);
      if (data.quote.contactInfoId) {
        const selectedContact = contactPersons.find((x) => x.webContactInfoId === data.quote.contactInfoId);
        resetLocalStateLens({
          ...(localStateLens |> get),
          contact: selectedContact,
          offerNotes: data.quote.offerNotes,
        });
      } else {
        resetLocalStateLens({
          ...(localStateLens |> get),
          offerNotes: data.quote.offerNotes,
        });
      }
    },
  });

  const [savePartnerContactPerson, { loading: saving }] = useMutationWithErrorHandling(SAVE_PARTNER_CONTACT_PERSON, {});

  const handleSaveContactPerson = useCallback(async () => {
    if (!validate(localStateLens.contact |> get)) return;

    const output = await savePartnerContactPerson({
      variables: {
        input: {
          quoteId: parseInt(quoteId),
          contactId:
            (localStateLens.contact.webContactInfoId |> get) < 0
              ? null
              : localStateLens.contact.webContactInfoId |> get,
          contactFirstName: localStateLens.contact.firstName |> get,
          contactLastName: localStateLens.contact.lastName |> get,
          contactEmail: localStateLens.contact.email |> get,
          contactAlternativeEmail: localStateLens.contact.alternativeEmail |> get,
          contactPhone: localStateLens.contact.phone |> get,
          comments: localStateLens.offerNotes |> get,
        },
      },
    });

    const newState = {
      ...(localStateLens |> get),
      contact: {
        ...(localStateLens.contact |> get),
        webContactInfoId: output.data.savePartnerContactPerson.contactInfoId,
      },
    };

    resetLocalStateLens(newState);
  }, [localStateLens, quoteId, savePartnerContactPerson, validate, resetLocalStateLens]);

  useEffect(() => {
    if ((localStateLens.contact.webContactInfoId |> get) !== -1) return;

    setPartnerContactPersons((prevPartnerContactPersons) => {
      const initialPartnerContactPersons = prevPartnerContactPersons.filter((x) => x.webContactInfoId !== -1);
      const newPartnerContactPersons = [...initialPartnerContactPersons, localStateLens.contact |> get];
      return newPartnerContactPersons;
    });
  }, [localStateLens.contact, setPartnerContactPersons]);

  const handleCreateNewContactPerson = useCallback(() => {
    const newContact = { webContactInfoId: -1, firstName: "", lastName: "", fullName: "" };
    (localStateLens.contact |> set)(newContact);
    validate(localStateLens.contact |> get);
  }, [localStateLens, validate]);

  return (
    <Card>
      <CardContent>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h4">
              {data?.quote?.partnerTypeId !== partnerTypes.individual
                ? t("DataCompletion.CompanyContactPerson")
                : t("DataCompletion.ContactPerson")}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              fullWidth
              isClearable
              valueKey="webContactInfoId"
              labelKey="fullName"
              options={partnerContactPersons}
              value={localStateLens?.contact |> get}
              onChange={localStateLens.contact |> set}
              disabled={partnerContactPersons.length < 1 || disabled}
              label={
                partnerContactPersons.length < 1
                  ? t("DataCompletion.NoContactsAvailable")
                  : t("DataCompletion.SelectContact")
              }
              noOptionsText={t("General.AutocompleteNoOptions")}
            />
          </Grid>
          <span>{t("Client.OR")}</span>
          <Grid item xs={6}>
            <Button variant="text" size="small" className={classes.lowercase} disabled={disabled}>
              <Link
                id={"create_new_contact_person"}
                onClick={handleCreateNewContactPerson}
                color="primary"
                underline="always"
              >
                {t("DataCompletion.CreateNewContactPerson")}
              </Link>
            </Button>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <CustomTextField
              label={t("DataCompletion.FirstName")}
              value={(localStateLens.contact?.firstName |> get) || emptyString}
              fullWidth
              required
              disabled={contactInfoReadOnly || disabled}
              error={!isValid(validation.firstName)}
              helperText={getErrors(validation.firstName)}
              onChange={localStateLens.contact.firstName |> set |> onTextBoxChange}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <CustomTextField
              label={t("DataCompletion.LastName")}
              value={(localStateLens.contact?.lastName |> get) || emptyString}
              fullWidth
              required
              disabled={contactInfoReadOnly || disabled}
              error={!isValid(validation.lastName)}
              helperText={getErrors(validation.lastName)}
              onChange={localStateLens.contact.lastName |> set |> onTextBoxChange}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <CustomTextField
              label={t("DataCompletion.Email")}
              value={(localStateLens.contact?.email |> get) || emptyString}
              fullWidth
              disabled={contactInfoReadOnly || disabled}
              onChange={localStateLens.contact.email |> set |> onTextBoxChange}
              error={!isValid(validation.email)}
              helperText={getErrors(validation.email)}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <CustomTextField
              label={t("DataCompletion.Phone")}
              value={(localStateLens.contact?.phone |> get) || emptyString}
              fullWidth
              disabled={contactInfoReadOnly || disabled}
              onChange={localStateLens.contact.phone |> set |> onTextBoxChange}
              error={!isValid(validation.phone)}
              helperText={getErrors(validation.phone)}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h4">{t("DataCompletion.Comment")}</Typography>
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              label={t("DataCompletion.EnterCommentsNotes")}
              value={(localStateLens.offerNotes |> get) || emptyString}
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              disabled={disabled}
              onChange={localStateLens.offerNotes |> set |> onTextBoxChange}
            />
          </Grid>
          <Grid item container xs={12} spacing={1} justifyContent="flex-end">
            <Button
              id="save_company_custom_data"
              onClick={handleSaveContactPerson}
              variant="contained"
              size="small"
              color="primary"
              disabled={!isDirty(localStateDirtyInfo) || disabled}
              endIcon={saving ? <CircularProgress size={15} color={"inherit"} /> : <CheckIcon />}
            >
              {t("General.Save")}
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <ActionsDialog
            open={actionsDialogProps.open}
            handleClose={handleCloseActionsDialog}
            title={actionsDialogProps.title}
            message={actionsDialogProps.message}
            actions={actionsDialogProps.actions}
            Content={actionsDialogProps.Content}
          />
        </Grid>
      </CardContent>
    </Card>
  );
}

CustomDataContainer.propTypes = {
  localStateLens: PropTypes.object.isRequired,
  disabled: PropTypes.bool.isRequired,
  localStateDirtyInfo: PropTypes.object.isRequired,
  resetLocalStateLens: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired,
  validate: PropTypes.func.isRequired,
};

export default CustomDataContainer;
